import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'common/dayjs';

/* API */
import PreviewingReservationService from 'api/PreviewingReservationService';
import { PreviewingReservationDetailResponseDto as PreviewingReservationDetail } from 'api/generated/openapi';

/* Layout */
import SecondaryButton from 'util/SecondaryButton';
import { Container, Row, Col } from 'react-bootstrap';
import LoadingSpinner from 'util/LoadingSpinner';
import InfoRow from 'layout/InfoRow';
import PrimaryButton from 'util/PrimaryButton';

const PreviewingReservationDetailComponent: React.FC = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [reservationDetail, setReservationDetail] = useState<PreviewingReservationDetail>();

  useEffect(() => {
    const fetchReservation = async () => {
      try {
        const reservationDetailResult = await PreviewingReservationService.getPreviewingReservation(
          { reservationId: reservationId || '' },
        );
        if (!reservationDetailResult.previewingReservationId) return;
        setReservationDetail(reservationDetailResult);
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReservation();
  }, []);

  const handleClickReservationCancel = () => {
    navigate(`/previewing-reservation-cancel/${reservationId}`);
  };

  const handleClickNavigateToQuestionnaire = () => {
    if (!reservationId || !reservationDetail) return;
    navigate(`/previewing-reservation-questionnaire/${reservationId}`);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (!reservationDetail) {
    navigate('/404');
  }

  const reservationDatetime = dayjs(reservationDetail?.reservationDateTime);
  const viewReservationDatetime = reservationDatetime?.format(
    `YYYY/MM/DD (${reservationDatetime.format('ddd')}) HH:mm〜`,
  );
  const viewTenpoName = `ホームメイト${reservationDetail?.tenpo.tenpoName}`;
  const tenpoUrl = reservationDetail?.tenpo.tenpoUrl;
  const viewTenpoPhone = reservationDetail?.tenpo.tenpoPhone;
  const viewTenpoEmail = reservationDetail?.tenpo.tenpoEmail;
  const isCancelled = reservationDetail?.reservationCancelled === 1;

  return (
    <>
      <Container className="pt-4 border-bottom">
        <InfoRow
          childrenLeft={<p className="fw-semibold">予約内容</p>}
          childrenRight={<p>内覧（{reservationDetail?.reservationTypeMinutes}分）</p>}
        />
        <InfoRow
          childrenLeft={<p className="fw-semibold">日 時</p>}
          childrenRight={<p>{viewReservationDatetime}</p>}
        />
        <InfoRow
          childrenLeft={<p className="fw-semibold">店 舗</p>}
          childrenRight={
            <>
              <a href={tenpoUrl} target="_blank">
                {viewTenpoName}
              </a>
              <ul>
                <li>
                  <a href={`tel:${viewTenpoPhone}`}>{viewTenpoPhone} </a>
                </li>
                <li>
                  <a href={`mailto:${viewTenpoEmail}`}>{viewTenpoEmail}</a>
                </li>
              </ul>
            </>
          }
        />
        {reservationDetail && !isCancelled && (
          <div className="text-center mx-4">
            <PrimaryButton
              label="事前アンケート"
              type="button"
              className="w-100 mt-3 mb-3"
              onClick={handleClickNavigateToQuestionnaire}
            />
          </div>
        )}
        <p className="fw-semibold mb-3">お問い合わせする物件</p>
        {reservationDetail?.bukkenSchedule.map((schedule) => {
          const address = [
            schedule.bukken.jusyo1,
            schedule.bukken.jusyo2,
            schedule.bukken.jusyo3,
            schedule.bukken.jusyo4,
          ]
            .filter(Boolean)
            .join(' ');
          const formatManEn = (value: number | null): string =>
            value != null ? `${value / 10000}万円` : '—';
          const homemateDetailUrl = `https://www.homemate.co.jp/dtl-${schedule.bukken.homemateBukkenNo}${schedule.heya.heyaNo}/`;
          const gaikanImagePath = schedule.bukken.gaikanImage?.gaikanImage1Path;
          const gaikanImageUrl = gaikanImagePath
            ? /^https?:\/\//.test(gaikanImagePath)
              ? gaikanImagePath
              : `https://ajic.homemate.co.jp/ice/${gaikanImagePath}`
            : undefined;
          const madori =
            schedule.heya.madoriSbName != null && schedule.heya.madoriSbCdName != null
              ? `${schedule.heya.madoriSbName}${schedule.heya.madoriSbCdName}`
              : null;
          const spec = [
            schedule.heya.yukaMensekiHeihou != null ? `${schedule.heya.yukaMensekiHeihou}㎡` : null,
            schedule.heya.kaisu != null ? `${schedule.heya.kaisu}階` : null,
          ]
            .filter(Boolean)
            .join(' / ');

          return (
            <a
              key={`${schedule.homemateBukkenNo}-${schedule.heyaNo}`}
              href={homemateDetailUrl}
              target="_blank"
              rel="noreferrer"
              className="d-block text-decoration-none text-reset py-3"
              style={{ borderTop: '1px solid #f75586', borderBottom: '1px solid #f75586' }}
            >
              <Row>
                <Col xs={6}>
                  {gaikanImageUrl ? (
                    <div className="ratio ratio-4x3">
                      <img
                        src={gaikanImageUrl}
                        alt=""
                        className="w-100 h-100"
                        style={{ objectFit: 'cover' }}
                      />
                    </div>
                  ) : (
                    <div className="ratio ratio-4x3 bg-light" />
                  )}
                </Col>
                <Col xs={6}>
                  <p className="mb-1">{schedule.bukken.bukkenName}</p>
                  <p className="mb-1">
                    {schedule.heya.kyoKanHiKb === '1' ? (
                      <>
                        <span className="text-primary">{formatManEn(schedule.heya.yatin)}</span>
                        {' / '}
                        {schedule.heya.kyoKanHiSelKbName} {formatManEn(schedule.heya.kyoKanHi)}
                      </>
                    ) : (
                      <span className="text-primary">{formatManEn(schedule.heya.yatin)}</span>
                    )}
                  </p>
                  <p className="mb-1">
                    {madori && <span className="text-primary">{madori}</span>}
                    {madori && spec && ' / '}
                    {spec}
                  </p>
                  <p className="mb-1">{address}</p>
                </Col>
              </Row>
            </a>
          );
        })}
        {(() => {
          if (!reservationDetail) return null;

          const c = reservationDetail.customer;
          if (!c || isCancelled) return null;

          const viewContractor =
            c.isContractor != null ? (c.isContractor ? '本人' : '本人ではない') : '';
          const viewBudget =
            c.budget != null && c.budget !== '' ? `${Number(c.budget).toLocaleString()}円` : '';
          const viewJob =
            c.job === 'その他' && c.jobOther != null && c.jobOther !== ''
              ? c.jobOther
              : c.job ?? '';
          const viewPreferred =
            c.isPreferredProperty != null
              ? c.isPreferredProperty
                ? '希望物件あり'
                : '来店のみ'
              : '';

          return (
            <>
              <InfoRow
                childrenLeft={<p className="fw-semibold">契約者</p>}
                childrenRight={<p>{viewContractor}</p>}
              />
              <InfoRow
                childrenLeft={<p className="fw-semibold">ご予算</p>}
                childrenRight={<p>{viewBudget}</p>}
              />
              <InfoRow
                childrenLeft={<p className="fw-semibold">ご職業</p>}
                childrenRight={<p>{viewJob}</p>}
              />
              <InfoRow
                childrenLeft={<p className="fw-semibold">希望物件</p>}
                childrenRight={<p>{viewPreferred}</p>}
              />
            </>
          );
        })()}
        {isCancelled && (
          <InfoRow
            childrenLeft={<p className="fw-semibold">予約状況</p>}
            childrenRight={<p>キャンセル済み</p>}
          />
        )}
      </Container>
      <Container className="pt-4">
        <Row className="justify-content-center">
          <Col sm={6} xs={12}>
            <p className="fw-semibold">注意事項</p>
            <ul>
              <li className="fw-semibold">予約日時の変更について</li>
              <p>
                不動産会社側の都合で予約日時の変更をお願いする場合があります。予めご了承ください。
              </p>
              <li className="fw-semibold">予約時間について</li>
              <p>
                当店は予約制のため、予約時間が経過した場合、内覧を終了させていただく場合がございます。
                また、ご予約の開始時間から30分経過しても、ご来店いただけず、ご連絡もない場合は予約キャンセルとさせていただくことがございます。{' '}
              </p>
              <li className="fw-semibold">予約の変更について</li>
              <p>現在の予約をキャンセルした上で、新たに予約を取得し直してください。</p>
              {!isCancelled && (
                <SecondaryButton
                  label="予約をキャンセルする"
                  type="button"
                  className="w-100 mt-1 mb-4"
                  onClick={handleClickReservationCancel}
                />
              )}
              <p>※予約当時のキャンセルは直接店舗にご連絡ください。</p>
            </ul>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PreviewingReservationDetailComponent;
